import { Card, Empty, Flex, message, Statistic, Table } from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { stockOverviewPrint } from "../../../components/print/stock-overview";
import API from "../../../services/api-request";
import TableColumn from "./columns";
import FilterMenu from "./filter-menu";
import { getDataLogin } from "../../../utils/getDataLogin";

const StockOverviewClient = () => {
  const [data, setData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  const queryParams = Object.fromEntries([...searchParams]);
  const pagination = {
    current: parseInt(searchParams.get("page")) || 1,
    limit: parseInt(searchParams.get("limit")) || 10,
    total: totalData,
    onChange: (page, limit) => {
      pagination.current = page;
      pagination.pageSize = limit;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", page);
      newSearchParams.set("limit", limit);
      setSearchParams(newSearchParams);
    },
  };

  const { id } = getDataLogin();

  const getStockData = async (query) => {
    setData([]);
    const queryString = new URLSearchParams(query).toString();

    if (!searchParams.get("client_id")) {
      searchParams.set("client_id", id);
    }

    // Add default page and limit
    if (!searchParams.has("limit")) {
      searchParams.set("limit", 10);
    }
    if (!searchParams.has("page")) {
      searchParams.set("page", 1);
    }
    setSearchParams(searchParams);

    setLoading(true);

    if (!queryString) return;

    API.Post(`dashboard/stocks${query ? "?" + queryString : ""}`)
      .then((res) => {
        const page = query.page ? parseInt(query.page) - 1 : 0;
        const limit = query.limit ? parseInt(query.limit) : 10;

        setTotalData(res?.data?.data?.total);

        const formatSelect = res?.data?.data?.stock?.map((item, index) => {
          return {
            ...item,
            no: page * limit + index + 1,
          };
        });

        setData(formatSelect);
        setTotalWeight(res?.data?.data?.weight);
        setTotalCost(res?.data?.data?.amount);
      })
      .catch((err) => {
        setData([]);
        message.info(`${err?.response?.data?.error}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const exportToExcel = async (columns) => {
    const headers = columns.map((col) => col.title);
    const dataKeys = columns.map((col) => col.dataIndex);
    const fileName = `${data?.[0]?.client_name} - Stock Overview.xlsx`;

    let error = false;
    let printData = [];

    const { received_at, client_id, sortBy, sortOrder } = queryParams;

    let url = `dashboard/stocks?limit=99999&page=1`;

    if (received_at) url += `&received_at=${received_at}`;
    if (client_id) url += `&client_id=${client_id}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortOrder) url += `&sortOrder=${sortOrder}`;

    await API.Post(url)
      .then((res) => {
        const data = res?.data?.data?.stock;

        const formattedPrintData = data.map((item, index) => ({
          ...item,
          no: index + 1,
          inbound_date: dayjs(item.inbound_date).format("YYYY-MM-DD"),
          duration: item.duration + " Hari",
          amount: item.amount,
          weight: item.weight,
          row_number: `${item.row_number}${item.section}-${item.column_name}${item.level}`,
        }));

        printData = formattedPrintData;
      })
      .catch((err) => {
        error = true;
        console.log(err);
        message.error("Failed to print data. Try again later");
      });

    if (error) return;

    const worksheetData = [
      [`WMS ELBE GROUP`, `Stock Overview`],
      [],
      [
        `Nama Client: ${data?.[0]?.client_name}`,
        `Total Berat: ${totalWeight.toLocaleString("id-ID")} Kg`,
        `Grand Total Biaya: Rp ${totalCost.toLocaleString("id-ID")}`,
      ],
      [],
      headers,
      ...printData.map((row) =>
        dataKeys.map((key) => {
          const value = row[key];
          return typeof value === "object" ? JSON.stringify(value) : value;
        })
      ),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    worksheet["!cols"] = columns.map((col) => ({
      wch: col.width ? col.width : 25,
    }));

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    if (!error) {
      saveAs(dataBlob, fileName);
    }
  };

  const exportToPDF = async () => {
    let error = false;
    let printData = [];

    const { received_at, client_id, sortBy, sortOrder } = queryParams;

    let url = `dashboard/stocks?limit=99999&page=1`;

    if (received_at) url += `&received_at=${received_at}`;
    if (client_id) url += `&client_id=${client_id}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortOrder) url += `&sortOrder=${sortOrder}`;

    await API.Post(url)
      .then((res) => {
        const data = res?.data?.data?.stock;

        const formattedPrintData = data.map((item, index) => ({
          ...item,
          no: index + 1,
          inbound_date: dayjs(item.inbound_date).format("YYYY-MM-DD"),
          duration: item.duration + " Hari",
          amount: item.amount,
          weight: item.weight,
          row_number: `${item.row_number}${item.section}-${item.column_name}${item.level}`,
        }));

        printData = formattedPrintData;
      })
      .catch((err) => {
        error = true;
        console.log(err);
        message.error("Failed to print data. Try again later");
      });

    if (error) return;

    const printWindow = window.open("", "_blank", "width=800,height=600");

    printWindow.document.open();
    printWindow.document.write(stockOverviewPrint(printData, "Client", id));
    setTimeout(function () {
      printWindow.document.close();
    }, 2000);

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print({ showPrintDialog: true });

      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  useEffect(() => {
    if (queryParams && id) {
      getStockData(queryParams);
    } else {
      setData([]);
    }
  }, [searchParams, id]);

  return (
    <>
      <Flex
        justify="flex-start"
        align="center"
        gap={16}
        style={{ marginBottom: "1rem" }}
      >
        <Card variant="borderless" style={{ width: "25%" }}>
          <Statistic
            title="Total Berat"
            value={totalWeight.toLocaleString("id-ID")}
            suffix="Kg"
          />
        </Card>

        <Card variant="borderless" style={{ width: "25%" }}>
          <Statistic
            title="Grand Total Biaya"
            value={totalCost.toLocaleString("id-ID")}
            prefix={"Rp"}
          />
        </Card>
      </Flex>

      <FilterMenu
        data={data}
        exportToExcel={exportToExcel}
        exportToPDF={exportToPDF}
        selectedFilter="Client"
        isClientUser
      />

      <Table
        loading={loading}
        columns={TableColumn(true, "Client")}
        dataSource={data}
        pagination={pagination}
        onChange={(pagination, filters, sorter, extra) => {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            if (sorter.order) {
              newParams.set("sortBy", sorter.field);
              newParams.set(
                "sortOrder",
                sorter.order === "ascend" ? "asc" : "desc"
              );
            } else {
              newParams.delete("sortBy");
              newParams.delete("sortOrder");
            }

            newParams.set("page", pagination.current);
            newParams.set("limit", pagination.pageSize);

            return newParams;
          });
        }}
        rowKey={(dataSource) => dataSource.no}
        locale={{
          emptyText: (
            <Empty
              description={
                !searchParams.get("client_id") && data.length === 0
                  ? "Please Select Client"
                  : "Data Empty"
              }
            />
          ),
        }}
      />
    </>
  );
};

export default StockOverviewClient;
